import React, { useEffect, useState, useContext } from 'react'
import { useNavigate } from 'react-router-dom'
import axios from '../../../../config/axios'
import ConfirmationModal from '../../../../components/ModalConfirmation'
import { FeedbackContext } from '../../../../contexts/FeedbackContext'
import {
  Container,
  HeaderDiv,
  HeaderText,
  CloseDiv,
  ContentContainer,
  Section,
  SectionHeader,
  DetailGrid,
  DetailItem,
  DetailLabel,
  DetailValue,
  ButtonContainer,
  EditButton,
  DeleteButton,
} from './style'
import { BlurBackground } from '../style'
import cross from '../../../../assets/addStockModal/cross.svg'
import editIcon from '../../../../assets/edit-pen-white.svg'
import trash from '../../../../assets/equipment/Tables/trash.svg'

const API_BASE_URL = `${process.env.REACT_APP_GATEWAY_BASE_URL}/backend-fashion-orders/fashion-product`

interface ViewProductCompositionModalProps {
  productId: number
  onClose: () => void
}

interface ProductDetailItem {
  label: string
  value: string
}

interface CompositionItemDetail {
  tipo: string
  material: string
  descricao: string
  resourceName?: string
  unidadeMedida: string
  localEstoque: string
  consumo: string

  colorsMap?: Record<string, string[]>
  colorMappings?: Array<{
    mainVariationId: number
    mainColorName: string
    itemVariationId: number | null
  }>
  materialId?: number
  resourceId?: number
}

interface FullProductData {
  general: ProductDetailItem[]
  grades: ProductDetailItem[]
  colors: ProductDetailItem[]

  collectionColors?: string[]
  composition: CompositionItemDetail[]
}

const ViewProductCompositionModal: React.FC<
  ViewProductCompositionModalProps
> = ({ productId, onClose }) => {
  const navigate = useNavigate()

  const { setFeedback } = useContext(FeedbackContext)
  const [productData, setProductData] = useState<FullProductData | null>(null)
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)
  const [allColors, setAllColors] = useState<{ id: number; name: string }[]>([])
  const [debugLogs, setDebugLogs] = useState<string[]>([])
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<boolean>(false)
  const debug = (msg: string) => {
    console.log(msg)
    setDebugLogs((prev) => [...prev, msg])
  }

  const fetchStockColors = async (stockId?: number, materialId?: number) => {
    console.debug('[VIEW] fetchStockColors', { stockId, materialId })
    try {
      const token = localStorage.getItem('token')
      const headers = token ? { Authorization: `Bearer ${token}` } : {}
      if (stockId) {
        const resp = await axios.get(`${API_BASE_URL}/stocks/${stockId}`, { headers })
        const stock = resp.data
        if (stock && stock.StockColors) {
          return stock.StockColors.map((sc: any) => ({ id: sc.colorId, name: sc.ItemColor?.name || `Cor ${sc.colorId}` }))
        }
      } else if (materialId) {
        const resp = await axios.get(`${API_BASE_URL}/stocks`, { headers, params: { materialId } })
        const stocks: any[] = Array.isArray(resp.data) ? resp.data : []
        const seen: Record<number, boolean> = {}
        const colors: any[] = []
        for (const stk of stocks) {
          if (stk.StockColors) {
            stk.StockColors.forEach((sc: any) => {
              if (!seen[sc.colorId]) {
                seen[sc.colorId] = true
                colors.push({ id: sc.colorId, name: sc.ItemColor?.name || `Cor ${sc.colorId}` })
              }
            })
          }
        }
        return colors
      }
    } catch (err) {
      console.error('Erro ao buscar cores do stock', err)
    }
    return []
  }

  const fetchResourceColors = async (resourceId: number) => {
    try {
      const token = localStorage.getItem('token')
      const headers = token ? { Authorization: `Bearer ${token}` } : {}
      const resp = await axios.get(`${API_BASE_URL}/resources/${resourceId}/colors`, { headers })
      const colors = Array.isArray(resp.data) ? resp.data : []
      return colors.map((rc: any) => ({ id: rc.itemColorId, name: rc.ItemColor?.name || `Cor ${rc.itemColorId}` }))
    } catch (err) {
      console.error('Erro ao buscar cores do recurso', err)
    }
    return []
  }

  useEffect(() => {
    debug('[VIEW] efeito ativado para productId=' + productId)
    const fetchProductData = async () => {
      if (!productId) {
        console.log('[VIEW] productId inválido, abortando fetch')
        setLoading(false)
        return
      }

      setLoading(true)
      setError(null)

      try {
        const token = localStorage.getItem('token')
        const headers = { Authorization: `Bearer ${token}` }
        debug('[VIEW] realizando GET product ' + productId)
        const response = await axios.get(`${API_BASE_URL}/${productId}`, {
          headers,
        })

        const data = response.data
        debug('[VIEW] resposta do GET produto recebida: ' + JSON.stringify(data))


        let localAllColors: { id: number; name: string }[] = []
        try {
          const colorsRes = await axios.get(`${API_BASE_URL}/colors`, { headers })
          const colorsArr = Array.isArray(colorsRes.data) ? colorsRes.data : []
          const simple = colorsArr.map((c: any) => ({ id: c.id, name: c.name }))
          localAllColors = simple
          setAllColors(simple)
          debug('[VIEW] allColors loaded early: ' + JSON.stringify(simple, null, 2))
        } catch (e) {
          console.warn('Erro ao buscar lista de cores antecipadamente', e)
        }

        const getColorNameFromId = (id?: number | null, fallbackVariations: any[] = []) => {
          if (!id) return null
          const idStr = String(id)
          const colorsSource = localAllColors.length ? localAllColors : allColors
          const foundInAllColors = colorsSource.find((c) => String(c.id) === idStr)
          if (foundInAllColors?.name) return foundInAllColors.name


          const variation = fallbackVariations.find((v: any) => String(v.colorId) === idStr || String(v.id) === idStr)
          if (variation?.color?.name) return variation.color.name

          return `Cor ${id}`
        }

        const collectionName = data.Collection?.name || 'N/A'
        const familyName = data.Family?.name || 'N/A'
        const groupName = data.Group?.name || 'N/A'

        const mappedGeneral: ProductDetailItem[] = [
          { label: 'ID', value: data.id?.toString() || 'N/A' },
          { label: 'Código', value: data.productCode || data.norm || 'N/A' },
          { label: 'Nome do Produto', value: data.name || 'N/A' },
          { label: 'Coleção', value: collectionName },
          { label: 'Família', value: familyName },
          { label: 'Grupo', value: groupName },
        ]

        const mappedGrades: ProductDetailItem[] =
          data.GradeInProduct && Array.isArray(data.GradeInProduct) && data.GradeInProduct.length > 0
            ? data.GradeInProduct
              .map((gp: any) => {
                const name = gp.Grade?.name || 'N/A'
                const type = gp.Grade?.GraduationType?.name || 'N/A'
                return `${type} - ${name}`
              })
              .map((str: string) => ({ label: 'Grades', value: str }))
            : [{ label: 'Grades', value: 'Nenhuma grade associada' }]


        let itemIdCounter = 1
        const scripts = data.Script || []
        const items: any[] = scripts.flatMap((script: any) => {
          const scriptItems: any[] = []

          if (script.ScriptMaterial && Array.isArray(script.ScriptMaterial)) {
            script.ScriptMaterial.forEach((sm: any) => {
              if (sm.Material?.id) {
                const material = sm.Material
                const stock = material.Stock?.[0]
                scriptItems.push({
                  id: itemIdCounter++,
                  tipo: 'materia-prima' as const,
                  materialId: material.id,
                  stockId: material.id,
                  resourceId: null,
                  descricao: '',
                  materialName: material?.name || stock?.description || '',
                  unidadeMedida: sm.UnitOfMeasurement?.name || '',
                  unitOfMeasurementId: sm.unitOfMeasurementId || null,
                  localEstoque: stock?.StockLocation?.name || '',
                  consumo: sm.weight?.toString() || sm.idealMeasurements?.toString() || '',
                  availableVariations: [],
                  colorMappings: [],
                  productItemColors: [],
                })
              }
            })
          }

          if (script.ScriptResource && Array.isArray(script.ScriptResource)) {
            script.ScriptResource.forEach((sr: any) => {
              if (sr.Resource?.id) {
                const resource = sr.Resource
                scriptItems.push({
                  id: itemIdCounter++,
                  tipo: 'insumo' as const,
                  stockId: null,
                  resourceId: resource.id,
                  materialId: resource.materialId || null,


                  materialName: resource.Material?.name || '',
                  descricao: resource.description || resource.name || '',
                  resourceName: resource.name || '',
                  resourceItemColorId: resource.ItemColor?.id || null,
                  resourceItemColorName: resource.ItemColor?.name || null,
                  unidadeMedida: sr.UnitOfMeasurement?.name || '',
                  unitOfMeasurementId: sr.unitOfMeasurementId || null,
                  localEstoque: '',
                  consumo: sr.weight?.toString() || '',
                  availableVariations: [],
                  colorMappings: [],
                  productItemColors: [],
                })
              }
            })
          }

          return scriptItems
        })

        const serverColors: any[] = data.productItemColors || data.ProductItemColor || []

        let itemsWithColors = items.map((item) => {
          const related = serverColors.filter((pc) => {
            if (item.stockId) {
              if (
                pc.materialId === item.stockId ||
                pc.MaterialColor?.Material?.id === item.stockId ||
                pc.stockId === item.stockId ||
                pc.StockColors?.stockId === item.stockId
              ) {
                return true
              }
            }


            if (item.materialId) {
              if (
                pc.materialId === item.materialId ||
                pc.StockColors?.Stock?.materialId === item.materialId ||
                pc.Resource?.materialId === item.materialId
              ) {
                return true
              }
            }

            if (item.resourceId) {
              if (
                pc.resourceId === item.resourceId ||
                pc.Resource?.id === item.resourceId
              ) {
                return true
              }
            }
            return false
          })
          const mapped = related.map((pc) => {
            const resolvedItemColorId =
              pc.itemColorId ??
              pc.Resource?.ItemColor?.id ??
              pc.StockColors?.ItemColor?.id ??
              pc.StockColors?.colorId
            return {
              collectionColorId: pc.collectionColorId,
              itemColorId: resolvedItemColorId,
              materialId: pc.materialId,
              resourceId: pc.resourceId,
            }
          })

          const newItem: any = { ...item, productItemColors: mapped, colorMappings: item.colorMappings }
          if (newItem.tipo === 'materia-prima') {
            const relWithStock = related.find((pc: any) => pc.stockId || pc.StockColors?.stockId)
            if (relWithStock) {
              const sid = relWithStock.stockId || relWithStock.StockColors?.stockId
              if (sid && sid !== newItem.stockId) {
                newItem.stockId = sid
                console.debug('[VIEW] assigned stockId from serverColors', newItem.stockId, 'for item', item.id)
              }
            }
          }
          return newItem
        })
        debug('[VIEW] itemsWithColors após anexar cores/service enrichment: ' + JSON.stringify(itemsWithColors, null, 2));
        debug('[VIEW] servidorColors utilizados: ' + JSON.stringify(serverColors, null, 2));
        debug('[VIEW] scripts originais: ' + JSON.stringify(scripts, null, 2));


        itemsWithColors = await Promise.all(itemsWithColors.map(async (it: any) => {
          if (it.tipo === 'materia-prima' && it.materialId) {
            if (!it.stockId || it.stockId === it.materialId) {
              try {
                const token = localStorage.getItem('token')
                const headers = token ? { Authorization: `Bearer ${token}` } : {}
                const res = await axios.get(`${API_BASE_URL}/stocks`, { headers, params: { materialId: it.materialId } })
                const stocks: any[] = Array.isArray(res.data) ? res.data : []
                if (stocks.length > 0) it.stockId = stocks[0].id
              } catch (e) {
                console.error('erro ao buscar stock para material (view modal)', e)
              }
            }
          }

          let variations: any[] = []
          if (it.tipo === 'materia-prima') {
            const stockIds = new Set<number>()
            if (it.stockId) stockIds.add(it.stockId)
              ; (it.productItemColors || []).forEach((pc: any) => {
                if (pc.stockId) stockIds.add(pc.stockId)
                else if (pc.StockColors?.stockId) stockIds.add(pc.StockColors.stockId)
              })
            console.debug('[VIEW] enriching material item', it.id, 'stockIds before fetch', Array.from(stockIds), 'materialId', it.materialId)
            if (stockIds.size) {
              for (const sid of stockIds) {
                const vs = await fetchStockColors(sid)
                variations.push(...vs)
              }
            } else if (it.materialId) {
              variations = await fetchStockColors(undefined, it.materialId)
            }
            variations = variations.filter((v, i, a) => a.findIndex(x => x.id === v.id) === i)
          } else if (it.resourceId) {
            variations = await fetchResourceColors(it.resourceId)
          }

          const mappedVars = variations.map(v => {
            const colorId = v.colorId ?? v.id;
            const colorName = v.color?.name || v.name || `Cor ${colorId}`;
            return {
              id: v.id,
              colorId,
              stockId: it.stockId || null,
              resourceId: it.resourceId || null,
              color: { id: colorId, name: colorName },
              stock: null,
              resource: null,
            };
          })
          const colorMappings = mappedVars.map(m => {
            let selectedVariation: number | null = null
            if (it.productItemColors && Array.isArray(it.productItemColors)) {
              const matching = it.productItemColors.find((pc: any) =>
                (pc.stockColorId && pc.stockColorId === m.id) ||
                (pc.itemColorId && pc.itemColorId === m.colorId)
              )
              if (matching) selectedVariation = m.id
            }
            return {
              mainVariationId: m.id,
              mainColorName: m.color.name,
              itemVariationId: selectedVariation,
            }
          })
          return { ...it, availableVariations: mappedVars, colorMappings }
        }))
        debug('[VIEW] itemsWithColors após enriquecimento completo: ' + JSON.stringify(itemsWithColors, null, 2));






        const collectionColorNameById: Record<number, string> = {}
        if (data.Collection && Array.isArray(data.Collection.CollectionColor)) {
          data.Collection.CollectionColor.forEach((cc: any) => {
            if (cc?.id && cc.name) collectionColorNameById[cc.id] = cc.name
          })
        }


        const collectionColorIdsUsed = new Set<number>()
        serverColors.forEach((pc) => {
          const cc = pc.CollectionColor || pc.collectionColor
          const id = cc?.id ?? pc.collectionColorId
          const name = cc?.name
          if (id) {
            collectionColorIdsUsed.add(id)
            if (name) collectionColorNameById[id] = name
          }
        })

        const finalCollectionColors = Array.from(collectionColorIdsUsed)
          .map((id) => ({ id, name: collectionColorNameById[id] || `Cor ${id}` }))

        const mappedComposition: CompositionItemDetail[] = itemsWithColors.map((item: any) => {
          const colorsMap: Record<string, string[]> = {}

          finalCollectionColors.forEach((col) => {
            colorsMap[col.name] = []
          })

          ;(item.productItemColors || []).forEach((pc: any) => {
            const colName = collectionColorNameById[pc.collectionColorId]
            if (!colName) return

            const colorId = pc.itemColorId ?? pc.stockColorId ?? null
            const colorName = colorId ? getColorNameFromId(colorId, item.availableVariations || []) : undefined
            if (!colorName) return

            if (!colorsMap[colName]) colorsMap[colName] = []
            if (!colorsMap[colName].includes(colorName)) {
              colorsMap[colName].push(colorName)
            }
          })

          return {
            tipo: item.tipo === 'materia-prima' ? 'Matéria-prima' : 'Insumo',
            material: item.materialName || '',
            descricao: item.descricao || '',
            unidadeMedida: item.unidadeMedida || 'N/A',
            localEstoque: item.localEstoque || '',
            consumo: item.consumo || '',
            colorsMap,
          }
        })


        console.debug('[VIEW] collectionColorIdsUsed', Array.from(collectionColorIdsUsed))
        console.debug('[VIEW] finalCollectionColors', finalCollectionColors)
        console.debug('[VIEW] itemsWithColors (productItemColors)', itemsWithColors.map((it: any) => ({
          id: it.id,
          tipo: it.tipo,
          productItemColors: it.productItemColors,
        })))

        debug('[VIEW] mappedComposition ' + JSON.stringify(mappedComposition, null, 2))

        const finalCollectionColorNames = finalCollectionColors.map((c) => c.name)

        const mappedColors: ProductDetailItem[] = [
          {
            label: 'Cores',
            value:
              finalCollectionColorNames.length > 0
                ? finalCollectionColorNames.join(', ')
                : 'Nenhuma cor associada',
          },
        ]

        setProductData({
          general: mappedGeneral,
          grades: mappedGrades,
          colors: mappedColors,
          collectionColors: finalCollectionColorNames,
          composition: mappedComposition,
        })
        setLoading(false)
      } catch (err: any) {
        console.error('Erro ao buscar detalhes do produto:', err)
        setError(
          err.response?.data?.message ||
          'Erro ao carregar dados do produto. Tente novamente.',
        )
        setLoading(false)
      }
    }

    fetchProductData()
  }, [productId])

  const handleClose = () => {
    onClose()
  }

  const handleEditProduct = () => {
    navigate(`/panel/product-composition/edit/${productId}`)
    onClose()
  }

  const handleDeleteProduct = () => {
    setShowDeleteConfirm(true)
  }

  const handleConfirmDeleteProduct = async () => {
    const token = localStorage.getItem('token')
    if (!token) {
      setFeedback({
        isOpen: true,
        statusOk: false,
        message: 'Sessão expirada. Faça login novamente.',
      })
      setShowDeleteConfirm(false)
      return
    }

    try {
      await axios.delete(`${API_BASE_URL}/${productId}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      setFeedback({
        isOpen: true,
        statusOk: true,
        message: 'Produto excluído com sucesso!',
      })
      onClose()
      window.location.reload()
    } catch (err: any) {
      console.error('Erro ao excluir produto:', err)
      setFeedback({
        isOpen: true,
        statusOk: false,
        message:
          err.response?.data?.message ||
          'Erro ao excluir produto. Tente novamente.',
      })
    } finally {
      setShowDeleteConfirm(false)
    }
  }

  if (loading) {
    return (
      <BlurBackground>
        <Container>
          <HeaderDiv>
            <HeaderText>Carregando dados do produto...</HeaderText>
            <CloseDiv onClick={handleClose}>
              <img src={cross} alt='Fechar' />
            </CloseDiv>
          </HeaderDiv>
          <ContentContainer>
            <div style={{ textAlign: 'center', padding: '2rem' }}>
              <p>Carregando informações do produto...</p>
            </div>
          </ContentContainer>
        </Container>
      </BlurBackground>
    )
  }

  if (error) {
    return (
      <BlurBackground>
        <Container>
          <HeaderDiv>
            <HeaderText>Erro ao carregar dados</HeaderText>
            <CloseDiv onClick={handleClose}>
              <img src={cross} alt='Fechar' />
            </CloseDiv>
          </HeaderDiv>
          <ContentContainer>
            <div style={{ textAlign: 'center', padding: '2rem' }}>
              <p style={{ color: 'red', marginBottom: '1rem' }}>{error}</p>
              <button onClick={handleClose}>Fechar</button>
            </div>
          </ContentContainer>
        </Container>
      </BlurBackground>
    )
  }

  if (!productData) {
    return (
      <BlurBackground>
        <Container>
          <HeaderDiv>
            <HeaderText>Produto não encontrado</HeaderText>
            <CloseDiv onClick={handleClose}>
              <img src={cross} alt='Fechar' />
            </CloseDiv>
          </HeaderDiv>
          <ContentContainer>
            <div style={{ textAlign: 'center', padding: '2rem' }}>
              Nenhum dado encontrado para o produto com ID: {productId}.
            </div>
          </ContentContainer>
        </Container>
      </BlurBackground>
    )
  }

  const productNameItem = productData.general.find(
    (item) => item.label === 'Nome do Produto',
  )
  const displayName = productNameItem
    ? productNameItem.value
    : `Produto #${productId}`

  return (
    <BlurBackground>
      <Container>
        <HeaderDiv>
          <HeaderText>Produto: {displayName}</HeaderText>
          <CloseDiv onClick={handleClose}>
            <img src={cross} alt='Fechar' />
          </CloseDiv>
        </HeaderDiv>

        <ContentContainer>

          <Section>
            <SectionHeader>Dados gerais</SectionHeader>
            <DetailGrid>
              {productData.general.map((item, index) => (
                <DetailItem key={index}>
                  <DetailLabel>{item.label}</DetailLabel>
                  <DetailValue>{item.value}</DetailValue>
                </DetailItem>
              ))}
            </DetailGrid>
          </Section>


          <Section>
            <SectionHeader>Grades</SectionHeader>
            <DetailGrid>
              {productData.grades.map((item, index) => (
                <DetailItem key={index}>
                  <DetailLabel>{item.value}</DetailLabel>
                </DetailItem>
              ))}
            </DetailGrid>
          </Section>


          <Section>
            <SectionHeader>Cores Coleção/Família</SectionHeader>
            <DetailGrid>
              {productData.colors.map((item, index) => (
                <DetailItem key={index}>
                  <DetailLabel>{item.value}</DetailLabel>
                </DetailItem>
              ))}
            </DetailGrid>
          </Section>


          <Section>
            <SectionHeader>Composição</SectionHeader>
            {productData.composition.length > 0 ? (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid #e0e0e0' }}>
                      <th
                        style={{
                          padding: '12px',
                          textAlign: 'left',
                          fontSize: '0.85vw',
                          fontWeight: 600,
                        }}
                      >
                        Tipo
                      </th>
                      <th
                        style={{
                          padding: '12px',
                          textAlign: 'left',
                          fontSize: '0.85vw',
                          fontWeight: 600,
                        }}
                      >
                        Material/Insumo
                      </th>
                      <th
                        style={{
                          padding: '12px',
                          textAlign: 'left',
                          fontSize: '0.85vw',
                          fontWeight: 600,
                        }}
                      >
                        Consumo
                      </th>
                      <th
                        style={{
                          padding: '12px',
                          textAlign: 'left',
                          fontSize: '0.85vw',
                          fontWeight: 600,
                        }}
                      >
                        Un. de medida
                      </th>
                      {productData.collectionColors && productData.collectionColors.length > 0 ? (
                        productData.collectionColors.map((colName, ci) => (
                          <th
                            key={ci}
                            style={{
                              padding: '12px',
                              textAlign: 'left',
                              fontSize: '0.85vw',
                              fontWeight: 600,
                            }}
                          >
                            {colName}
                          </th>
                        ))
                      ) : (

                        <th
                          style={{
                            padding: '12px',
                            textAlign: 'left',
                            fontSize: '0.85vw',
                            fontWeight: 600,
                          }}
                        >
                          Cores
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {productData.composition.map((item, index) => (
                      <tr
                        key={index}
                        style={{ borderBottom: '1px solid #f0f0f0' }}
                      >
                        <td style={{ padding: '12px', fontSize: '0.85vw' }}>
                          {item.tipo}
                        </td>
                        <td style={{ padding: '12px', fontSize: '0.85vw' }}>
                          {item.material}
                        </td>
                        <td style={{ padding: '12px', fontSize: '0.85vw' }}>
                          {item.consumo}
                        </td>
                        <td style={{ padding: '12px', fontSize: '0.85vw' }}>
                          {item.unidadeMedida}
                        </td>
                        { }
                        {productData.collectionColors && productData.collectionColors.length > 0 ? (
                          productData.collectionColors.map((colName, ci) => (
                            <td key={ci} style={{ padding: '12px', fontSize: '0.85vw' }}>
                              {item.colorsMap && item.colorsMap[colName]
                                ? item.colorsMap[colName].length > 0
                                  ? item.colorsMap[colName].join(', ')
                                  : '-'
                                : '-'}
                            </td>
                          ))
                        ) : (

                          <td style={{ padding: '12px', fontSize: '0.85vw' }}>
                            {item.colorsMap
                              ? Object.entries(item.colorsMap)
                                .map(([k, v]) => `${k}: ${v.length > 0 ? v.join(', ') : '-'}`)
                                .join(' | ')
                              : '-'}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p style={{ fontSize: '0.9vw', color: '#666' }}>
                Nenhum item de composição cadastrado.
              </p>
            )}
          </Section>
        </ContentContainer>

        <ButtonContainer>
          <DeleteButton onClick={handleDeleteProduct}>
            <img src={trash} alt='Excluir' />
            Excluir produto
          </DeleteButton>
          <EditButton style={{ marginLeft: '1rem' }} onClick={handleEditProduct}>
            <img src={editIcon} alt='Editar' />
            Editar produto
          </EditButton>
        </ButtonContainer>

        <ConfirmationModal
          isOpen={showDeleteConfirm}
          title="Tem certeza que deseja excluir este produto?"
          message="Essa ação não poderá ser desfeita."
          actions={[
            {
              label: 'Não, voltar',
              onClick: () => setShowDeleteConfirm(false),
              variant: 'outline',
            },
            {
              label: 'Sim, excluir',
              onClick: handleConfirmDeleteProduct,
              variant: 'danger',
            },
          ]}
        />
      </Container>
    </BlurBackground>
  )
}

export default ViewProductCompositionModal